import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

// ── Bundled translation resources ────────────────────────────────────────────
import enCommon from '@/locales/en/common.json';
import enAuth from '@/locales/en/auth.json';
import enDashboard from '@/locales/en/dashboard.json';
import enBots from '@/locales/en/bots.json';
import enRisk from '@/locales/en/risk.json';
import enSafety from '@/locales/en/safety.json';
import enSystem from '@/locales/en/system.json';
import enSettings from '@/locales/en/settings.json';
import enGlossary from '@/locales/en/glossary.json';
import enBacktests from '@/locales/en/backtests.json';
import enAudit from '@/locales/en/audit.json';
import enOrders from '@/locales/en/orders.json';
import enAlerts from '@/locales/en/alerts.json';
import enVenueConnections from '@/locales/en/venue-connections.json';
import enExecutionProfiles from '@/locales/en/execution-profiles.json';
import enStrategies from '@/locales/en/strategies.json';
import enWallets from '@/locales/en/wallets.json';
import enMarketData from '@/locales/en/market-data.json';
import enAdmin from '@/locales/en/admin.json';
import enFeedback from '@/locales/en/feedback.json';
import enErrors from '@/locales/en/errors.json';
import enEmpty from '@/locales/en/empty.json';
import enOnboarding from '@/locales/en/onboarding.json';
import enVenuesWizard from '@/locales/en/venues-wizard.json';
import enLanding from '@/locales/en/landing.json';

import ptBRCommon from '@/locales/pt-BR/common.json';
import ptBRAuth from '@/locales/pt-BR/auth.json';
import ptBRDashboard from '@/locales/pt-BR/dashboard.json';
import ptBRBots from '@/locales/pt-BR/bots.json';
import ptBRRisk from '@/locales/pt-BR/risk.json';
import ptBRSafety from '@/locales/pt-BR/safety.json';
import ptBRSystem from '@/locales/pt-BR/system.json';
import ptBRSettings from '@/locales/pt-BR/settings.json';
import ptBRGlossary from '@/locales/pt-BR/glossary.json';
import ptBRBacktests from '@/locales/pt-BR/backtests.json';
import ptBRAudit from '@/locales/pt-BR/audit.json';
import ptBROrders from '@/locales/pt-BR/orders.json';
import ptBRAlerts from '@/locales/pt-BR/alerts.json';
import ptBRVenueConnections from '@/locales/pt-BR/venue-connections.json';
import ptBRExecutionProfiles from '@/locales/pt-BR/execution-profiles.json';
import ptBRStrategies from '@/locales/pt-BR/strategies.json';
import ptBRWallets from '@/locales/pt-BR/wallets.json';
import ptBRMarketData from '@/locales/pt-BR/market-data.json';
import ptBRAdmin from '@/locales/pt-BR/admin.json';
import ptBRFeedback from '@/locales/pt-BR/feedback.json';
import ptBREmpty from '@/locales/pt-BR/empty.json';
import ptBROnboarding from '@/locales/pt-BR/onboarding.json';
import ptBRVenuesWizard from '@/locales/pt-BR/venues-wizard.json';
import ptBRLanding from '@/locales/pt-BR/landing.json';

// ── Types ────────────────────────────────────────────────────────────────────
export const supportedLanguages = ['en', 'pt-BR'] as const;
export type SupportedLanguage = (typeof supportedLanguages)[number];

export const languageLabels: Record<SupportedLanguage, string> = {
  en: 'English',
  'pt-BR': 'Português (BR)',
};

// ── Resources ────────────────────────────────────────────────────────────────
const resources = {
  en: {
    common: enCommon,
    auth: enAuth,
    dashboard: enDashboard,
    bots: enBots,
    risk: enRisk,
    safety: enSafety,
    system: enSystem,
    settings: enSettings,
    glossary: enGlossary,
    backtests: enBacktests,
    audit: enAudit,
    orders: enOrders,
    alerts: enAlerts,
    'venue-connections': enVenueConnections,
    'execution-profiles': enExecutionProfiles,
    strategies: enStrategies,
    wallets: enWallets,
    'market-data': enMarketData,
    admin: enAdmin,
    feedback: enFeedback,
    errors: enErrors,
    empty: enEmpty,
    onboarding: enOnboarding,
    'venues-wizard': enVenuesWizard,
    landing: enLanding,
  },
  'pt-BR': {
    common: ptBRCommon,
    auth: ptBRAuth,
    dashboard: ptBRDashboard,
    bots: ptBRBots,
    risk: ptBRRisk,
    safety: ptBRSafety,
    system: ptBRSystem,
    settings: ptBRSettings,
    glossary: ptBRGlossary,
    backtests: ptBRBacktests,
    audit: ptBRAudit,
    orders: ptBROrders,
    alerts: ptBRAlerts,
    'venue-connections': ptBRVenueConnections,
    'execution-profiles': ptBRExecutionProfiles,
    strategies: ptBRStrategies,
    wallets: ptBRWallets,
    'market-data': ptBRMarketData,
    admin: ptBRAdmin,
    feedback: ptBRFeedback,
    // pt-BR `errors` namespace falls back to EN per checklist (translator pass deferred).
    errors: enErrors,
    empty: ptBREmpty,
    onboarding: ptBROnboarding,
    'venues-wizard': ptBRVenuesWizard,
    landing: ptBRLanding,
  },
} as const;

// ── Read persisted language (Zustand persist format) ─────────────────────────
function getPersistedLanguage(): SupportedLanguage {
  try {
    const stored = localStorage.getItem('loki-language');
    if (stored) {
      const parsed = JSON.parse(stored) as { state?: { language?: string } };
      const lang = parsed?.state?.language;
      if (lang === 'en' || lang === 'pt-BR') return lang;
    }
  } catch {
    /* noop */
  }
  return 'en';
}

// ── Initialize i18next ───────────────────────────────────────────────────────
void i18n.use(initReactI18next).init({
  resources,
  lng: getPersistedLanguage(),
  fallbackLng: 'en',
  defaultNS: 'common',
  ns: [
    'common',
    'auth',
    'dashboard',
    'bots',
    'risk',
    'safety',
    'system',
    'settings',
    'glossary',
    'backtests',
    'audit',
    'orders',
    'alerts',
    'venue-connections',
    'execution-profiles',
    'strategies',
    'wallets',
    'market-data',
    'admin',
    'feedback',
    'errors',
    'empty',
    'onboarding',
    'venues-wizard',
    'landing',
  ],
  interpolation: {
    escapeValue: false, // React handles XSS
  },
  react: {
    useSuspense: false, // Translations are bundled, no async loading needed
  },
});

export default i18n;
